import { useI18N } from "common/hooks";
import { cn } from "@/lib/utils";
import type { MapMode } from "./useMapMode";

type Props = {
  mapMode: MapMode;
  onChange: (mode: MapMode) => void;
  /** Which segments to show (in order). Defaults to sociability + all. */
  modes?: MapMode[];
  className?: string;
};

/**
 * Segmented map-mode toggle ("Places with data" / "Explore" / "All places").
 * The active segment is shown with a blue outline (not a filled background).
 */
export function MapModeToggle({
  mapMode,
  onChange,
  modes = ["sociability", "all"],
  className,
}: Props) {
  const i18n = useI18N();
  const labels: Record<MapMode, string> = {
    sociability: i18n.get("placesWithData") || "Places with data",
    overview: i18n.get("mapModeOverview") || "Overview",
    declutter: i18n.get("mapModeExplore") || "Explore",
    all: i18n.get("allPlaces") || "All places",
  };
  const options = modes.map((value) => ({ value, label: labels[value] }));

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full border border-border-token-neutral-minimal bg-bg-brand-secondary-default p-1 shadow-sm",
        className,
      )}
    >
      {options.map((opt) => {
        const active = mapMode === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              "px-4 py-2 rounded-full label-m-semibold border-2 transition-colors",
              active
                ? "border-fg-brand-subtle text-fg-brand-subtle bg-bg-brand-secondary-default"
                : "border-transparent text-fg-neutral-subtle hover:text-fg-neutral-strong",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
